import { MIN_SELECTABLE_REGISTRATION_YEAR } from '../../helpers/parseCarFormQuery'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ReactNode } from 'react'
import invariant from 'tiny-invariant'
import { UrlObject } from 'url'
import useHtmlClassNames from '@/helpers/useHtmlClassNames'
import { useNavigateToPathBookmark } from '@/helpers/usePathBookmarks'
import { useSearchParams } from '@/helpers/useSearchParams'
import BackIcon from '@/icons/24/chevron-left.svg'
import CloseIcon from '@/icons/24/cross.svg'
import Logo from '@/icons/logo-with-name-horizontal.svg'

export interface CarFormStepProps {
  step: number
  children?: ReactNode
}

export default function CarFormStep({ step, ...props }: CarFormStepProps) {
  useHtmlClassNames('bg-fill-senary')
  const { t } = useTranslation('carPawnForm')

  const {
    betrag,
    marke: make,
    modell: model,
    zulassungsjahr: registrationYear,
  } = useSearchParams()

  invariant(!Array.isArray(make))
  invariant(!Array.isArray(model))
  invariant(!Array.isArray(registrationYear))

  return (
    <div className="overflow-x-clip">
      <CarFormHeader />

      <div className="pt-12 pb-[76px]">
        <div className="flex justify-center flex-initial w-screen">
          <div className="flex flex-col gap-6 px-4 py-6 w-screen max-w-[600px]">
            {props.children}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center bg-fill-senary fixed inset-x-0 bottom-0">
        <div className="h-1 flex w-full">
          <div
            className="h-full bg-fill-brand-primary"
            style={{ flexGrow: step }}
          />
          <div
            className="h-full bg-fill-brand-tertiary"
            style={{ flexGrow: 5 - step }}
          />
        </div>

        <div className="flex px-2 text-center w-full max-w-[800px]">
          <FooterStep
            label={t('make')}
            value={step > 1 && make}
            href={{ pathname: '/autopfand-formular', query: { betrag } }}
          />

          <FooterStep
            label={t('model')}
            value={step > 2 && model}
            href={{
              pathname: '/autopfand-formular/modell',
              query: { betrag, marke: make },
            }}
          />

          <FooterStep
            label={t('year')}
            value={
              step > 3 &&
              (registrationYear !== 'before-range'
                ? registrationYear
                : t('before_year', { year: MIN_SELECTABLE_REGISTRATION_YEAR }))
            }
            href={{
              pathname: '/autopfand-formular/zulassungsjahr',
              query: { betrag, marke: make, modell: model },
            }}
          />
        </div>
      </div>
    </div>
  )
}

export function CarFormHeader({
  hideBackButton,
}: {
  hideBackButton?: boolean
}) {
  const router = useRouter()
  const navigateToPathBookmark = useNavigateToPathBookmark('/autopfand')

  return (
    <div className="bg-fill-senary fixed px-2 top-0 z-10 w-full flex justify-between items-center border-b border-b-secondary px-s">
      {!hideBackButton ? (
        <button
          onClick={() => {
            router.back()
          }}
        >
          <BackIcon className="m-3" />
        </button>
      ) : (
        <div className="w-12" />
      )}

      <Link href="/">
        <Logo />
      </Link>

      <button onClick={navigateToPathBookmark}>
        <CloseIcon className="m-3" />
      </button>
    </div>
  )
}

function FooterStep(props: {
  label: string
  value: ReactNode | false
  href: UrlObject | string
}) {
  const Comp = props.value !== false ? Link : 'div'

  return (
    <Comp className="flex-1 py-4 min-w-0 px-1" href={props.href}>
      <span className="uppercase text-tertiary text-heading-2xs block">
        {props.label}
      </span>

      <span className="block max-w-full overflow-hidden whitespace-nowrap text-ellipsis">
        {props.value ? props.value : '–'}
      </span>
    </Comp>
  )
}
